// Single source of truth for which languages catalog overrides support.
//
// Every catalog column carries a base label per language as flat, suffixed
// keys — `headerName`/`headerNameES` for headers, `description`/`descriptionES`
// for descriptions. Overrides mirror those same keys. Historically both the
// store's overridable-field list and the admin editor's columns hardcoded that
// EN/ES pair, so adding a third language meant editing several files.
//
// Instead, describe each locale once here and derive everything from it:
//   - `code`      the i18n locale code the app runs under (see useI18n)
//   - `label`     short chip label for the editor's language switcher
//   - `name`      full language name, used in placeholders/headers
//   - `headerKey` the column/override key holding this locale's header label
//   - `descKey`   the column/override key holding this locale's description
//   - `fallback`  (optional) the locale to try before English when a column
//                 has no label for this one
//
// Adding a language is now one entry (plus a corresponding `headerName<XX>` /
// `description<XX>` on the catalog columns, and the app locale itself).
export const CATALOG_LOCALES = [
  {
    code: 'en',
    label: 'EN',
    name: 'English',
    headerKey: 'headerName',
    descKey: 'description',
  },
  {
    code: 'es',
    label: 'ES',
    name: 'Español',
    headerKey: 'headerNameES',
    descKey: 'descriptionES',
  },
  {
    code: 'fr',
    label: 'FR',
    name: 'Français',
    headerKey: 'headerNameFR',
    descKey: 'descriptionFR',
  },
  {
    code: 'fr-ca',
    label: 'FR-CA',
    name: 'Français (Canada)',
    headerKey: 'headerNameFRCA',
    descKey: 'descriptionFRCA',
    fallback: 'fr',
  },
];

// Every override key across all locales, both headers and descriptions. This is
// what the store persists and what override-merging iterates.
export const OVERRIDABLE_FIELDS = CATALOG_LOCALES.flatMap((l) => [
  l.headerKey,
  l.descKey,
]);

// Fall back to the first locale (English) when an unknown code is passed, so a
// missing/typo'd locale still resolves to a real key rather than undefined.
function localeFor(code) {
  return CATALOG_LOCALES.find((l) => l.code === code) || CATALOG_LOCALES[0];
}

export function headerKeyForLocale(code) {
  return localeFor(code).headerKey;
}

export function descKeyForLocale(code) {
  return localeFor(code).descKey;
}

// A catalog column's header label in the given locale. A column without one
// falls back to the locale's `fallback` (Canadian French to French), then
// to the English label.
export function headerLabelFor(col, code) {
  if (!col) return '';
  const { headerKey, fallback } = localeFor(code);
  const fallbackLabel = fallback ? col[headerKeyForLocale(fallback)] : '';
  return col[headerKey] || fallbackLabel || col.headerName || '';
}
